import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Bell, Building2, ChevronsLeft, ChevronsRight, ClipboardList, DollarSign,
  FileBarChart, FileText, Gauge, GitCompare, History, LayoutDashboard, Leaf, LogOut,
  Package, PackageSearch, Settings, Sparkles, TrendingUp, Truck, Users, Wrench, FlaskConical, BookOpen, Building
} from "lucide-react";
import BrandMark from "@/components/app/BrandMark";
import { ROLES, useAuth, canEditProduction, canApprove } from "@/lib/auth";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const NAV = [
  { section: "Utama", items: [
    { to: "/app", end: true, icon: LayoutDashboard, label: "Dashboard" },
  ]},
  { section: "Master Data", items: [
    { to: "/app/master/products", icon: Package, label: "Produk" },
    { to: "/app/master/materials", icon: PackageSearch, label: "Bahan Baku" },
    { to: "/app/master/suppliers", icon: Truck, label: "Supplier" },
    { to: "/app/master/labor", icon: Users, label: "Tenaga Kerja" },
    { to: "/app/master/machines", icon: Wrench, label: "Mesin" },
    { to: "/app/master/units", icon: FileText, label: "Satuan" },
    { to: "/app/master/locations", icon: Building2, label: "Lokasi" },
    { to: "/app/master/overhead", icon: Gauge, label: "Kategori Overhead" },
    { to: "/app/master/env-categories", icon: Leaf, label: "Kategori Lingkungan" },
  ]},
  { section: "Produksi", items: [
    { to: "/app/mix-designs", icon: FlaskConical, label: "Mix Design" },
    { to: "/app/batches", icon: ClipboardList, label: "Batch Produksi" },
    { to: "/app/env-costs", icon: Leaf, label: "Biaya Lingkungan" },
  ]},
  { section: "Analisis", items: [
    { to: "/app/hpp", icon: TrendingUp, label: "Perhitungan HPP" },
    { to: "/app/pricing", icon: DollarSign, label: "Simulasi Harga Jual" },
    { to: "/app/comparison", icon: GitCompare, label: "Perbandingan Produk" },
    { to: "/app/references", icon: BookOpen, label: "Referensi Data" },
    { to: "/app/reports", icon: FileBarChart, label: "Laporan" },
  ]},
  { section: "Administrasi", items: [
    { to: "/app/organizations", icon: Building, label: "Perusahaan Pengguna", superAdminOnly: true },
    { to: "/app/members", icon: Users, label: "Anggota Organisasi" },
    { to: "/app/settings", icon: Settings, label: "Pengaturan" },
    { to: "/app/audit", icon: History, label: "Audit Log" },
  ]},
];

function OrgSwitcher() {
  const { user, orgs, switchOrg } = useAuth();
  const [open, setOpen] = useState(false);
  const activeOrg = orgs.find((o) => o.id === user?.active_org_id);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-2 h-10 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-semibold transition-colors" data-testid="org-switcher">
          <Building2 className="w-4 h-4 text-emerald-600" />
          <div className="flex flex-col text-left leading-tight">
            <span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Perusahaan Pengguna</span>
            <span className="max-w-[220px] truncate">{activeOrg?.name || "Pilih Perusahaan"}</span>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2">
        <div className="px-2 py-1.5 gc-label">Perusahaan Pengguna Aktif</div>
        <div className="max-h-72 overflow-auto">
          {orgs.map((o) => (
            <button key={o.id} onClick={async () => { await switchOrg(o.id); setOpen(false); window.location.reload(); }}
              className={`w-full text-left px-2 py-2 rounded-lg text-sm hover:bg-slate-100 flex flex-col ${o.id === user?.active_org_id ? "bg-emerald-50 text-emerald-800" : ""}`}
              data-testid={`org-${o.slug}`}>
              <span className="font-semibold">{o.name}</span>
              <span className="text-[11px] text-slate-500">{o.is_demo ? "DATA DEMO — pengguna sistem" : "Perusahaan pengguna"}</span>
            </button>
          ))}
          {orgs.length === 0 && <div className="p-3 text-sm text-slate-500">Belum ada perusahaan pengguna.</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-2.5 h-10 px-2 pr-3 rounded-full bg-white border border-slate-200 hover:border-emerald-400 transition-colors" data-testid="user-menu">
          <span className="h-7 w-7 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white text-xs font-bold flex items-center justify-center">
            {(user?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
          </span>
          <div className="text-left leading-tight hidden sm:block">
            <div className="text-xs font-semibold text-slate-900 max-w-[150px] truncate">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">{ROLES[user?.active_role] || ROLES[user?.platform_role] || "-"}</div>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <div className="p-3 border-b border-slate-100">
          <div className="font-semibold text-slate-900">{user?.name}</div>
          <div className="text-xs text-slate-500">{user?.email}</div>
        </div>
        <button onClick={() => navigate("/app/settings")} className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-slate-100 flex items-center gap-2"><Settings className="w-4 h-4" /> Pengaturan</button>
        <button onClick={async () => { await logout(); navigate("/"); }} className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-rose-50 text-rose-700 flex items-center gap-2" data-testid="btn-logout"><LogOut className="w-4 h-4" /> Keluar</button>
      </PopoverContent>
    </Popover>
  );
}

export default function DashboardShell() {
  const [collapsed, setCollapsed] = useState(false);
  const { user } = useAuth();
  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className={`${collapsed ? "w-20" : "w-64"} flex-shrink-0 hidden md:flex flex-col bg-[#0B192C] text-slate-200 transition-all duration-300 sticky top-0 h-screen`}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          {!collapsed && <BrandMark variant="light" withInstitution />}
          <button onClick={() => setCollapsed((c) => !c)} className="p-2 rounded-lg hover:bg-white/5 text-slate-400" data-testid="sidebar-toggle">
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
          {NAV.map((sec) => {
            const items = sec.items.filter((it) => !it.superAdminOnly || user?.platform_role === "super_admin");
            if (items.length === 0) return null;
            return (
              <div key={sec.section}>
                {!collapsed && <div className="px-4 mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{sec.section}</div>}
                <div className="space-y-1">
                  {items.map((it) => (
                    <NavLink key={it.to} to={it.to} end={it.end}
                      className={({ isActive }) => `gc-nav-item ${isActive ? "active" : ""} ${collapsed ? "justify-center" : ""}`}
                      title={it.label} data-testid={`nav-${it.label}`}>
                      <it.icon className="w-4 h-4 flex-shrink-0" />
                      {!collapsed && <span>{it.label}</span>}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
        <div className={`p-3 border-t border-slate-800 ${collapsed ? "text-center" : ""}`}>
          {!collapsed && (
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-3">
              <div className="flex items-center gap-2 text-emerald-300 text-xs font-bold uppercase tracking-widest"><Sparkles className="w-3.5 h-3.5" /> Produk Inovasi</div>
              <p className="mt-1 text-[11px] text-slate-300">Politeknik Negeri Manado — Sistem hilirisasi penelitian.</p>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <OrgSwitcher />
          </div>
          <div className="flex items-center gap-3">
            <button className="h-10 w-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-emerald-600 hover:border-emerald-400 transition-colors" data-testid="btn-notifications" title="Notifikasi">
              <Bell className="w-4 h-4" />
            </button>
            <UserMenu />
          </div>
        </header>

        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
